import * as React from 'react';

import {
  emotionStyled,
  SizeContextSizeValue,
  tokens,
  useSize,
} from '@packages/utilities/styled';

import {emptyObject} from '@packages/utilities/fp/utils';

import {Note} from '../../Note';
import {NoteProps} from '../../Note/Note';
import {Portal} from '../../Portal';

import {MenuDivider} from '../MenuDivider';
import {useMenuContentContext} from '../useMenuContent';
import {MenuNoteContext} from './useMenuNote';

type MenuNoteProps = NoteProps & {
  _isShownOnHover?: boolean;
};

const NoteView = emotionStyled(Note)({
  padding: tokens.ui.menu.item.padding,
});

const MEDIUM_MENU_DIVIDER_MARGIN = 4;
const X_LARGE_MENU_DIVIDER_MARGIN = 8;
const MENU_DIVIDER_HEIGHT = 1;

const getMenuDividerHeight = (size: SizeContextSizeValue) => {
  if (size === 'x-large') {
    return X_LARGE_MENU_DIVIDER_MARGIN * 2 + MENU_DIVIDER_HEIGHT;
  } else {
    return MEDIUM_MENU_DIVIDER_MARGIN * 2 + MENU_DIVIDER_HEIGHT;
  }
};

export const MenuNote = ({
  children,
  hasIcon = false,
  _isShownOnHover: isShownOnHover = false,
  ...props
}: MenuNoteProps) => {
  const menuNoteRef = React.useRef<HTMLDivElement>(null);
  const {menuItemNote, notePortalRef, setStaticMenuNoteHeight} =
    useMenuContentContext();
  /**
   * If a MenuItem note is being rendered (i.e. if menuItemNote is not null), and
   * if isShownOnHover is false for the current MenuNote (i.e. the MenuNote is static)
   * then we visually hide the current MenuNote.
   *
   * We do this so any underlying MenuNoteAction's can still be keyboard accessible in the activeIndex sequence.
   */
  const shouldHideVisually = menuItemNote && !isShownOnHover;

  const size = useSize();
  const menuNoteHeight =
    getMenuDividerHeight(size) +
    (menuNoteRef?.current?.getBoundingClientRect().height || 0);

  React.useEffect(() => {
    if (!isShownOnHover) {
      setStaticMenuNoteHeight(menuNoteHeight);
    }
  }, [isShownOnHover, menuNoteHeight, setStaticMenuNoteHeight]);

  if (!notePortalRef?.current) {
    return null;
  }

  return (
    <Portal root={notePortalRef?.current}>
      <MenuNoteContext.Provider value={{hasMenuNoteContext: true}}>
        <div
          style={
            shouldHideVisually
              ? {
                  opacity: 0,
                  position: 'absolute',
                }
              : emptyObject
          }
        >
          <MenuDivider />
          <NoteView
            hasIcon={hasIcon}
            id="spring-menu-note"
            isGhost
            ref={menuNoteRef}
            role={isShownOnHover ? 'tooltip' : 'note'}
            {...props}
          >
            {children}
          </NoteView>
        </div>
      </MenuNoteContext.Provider>
    </Portal>
  );
};

MenuNote.displayName = 'MenuNote';

export default MenuNote;
